import { PromptUsageResponseSchema } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";

// ─── Types ───────────────────────────────────────────────────────────

export interface PromptVersionDto {
  id: string;
  version: number;
  content: string;
  /** Null = service default at call sites. */
  model: string | null;
  createdBy: string | null;
  createdAt: string;
  variables: string[];
}

export interface PromptLlmModelDto {
  id: string;
  label: string;
  provider: "anthropic" | "google";
  family: "claude" | "gemini";
  tier: "economy" | "standard" | "premium";
  costHint: "$" | "$$" | "$$$";
  capabilities: Array<"text" | "vision" | "image">;
  /** Listed by Vertex but not invokable by this project — shown locked. */
  unavailable?: boolean;
  inputPerMillion?: number | null;
  outputPerMillion?: number | null;
}

export interface PromptServiceDefaultModelDto {
  /** Null when the call site uses a fixed package default and no env key. */
  configKey: string | null;
  resolved: string | null;
}

export interface PromptUsageRowDto {
  promptId: string;
  calls: number;
  totalTokens: number;
  costMicros: number;
  unpricedTokens: number;
  avgLatencyMs: number | null;
  p95LatencyMs: number | null;
  latestVersionCalls: number;
}

export interface PromptUsageResponseDto {
  windowHours: number;
  items: PromptUsageRowDto[];
}

export interface PromptLlmModelsResponseDto {
  models: PromptLlmModelDto[];
  fetchedAt: string | null;
  fromBootstrap: boolean;
}

export type PromptTaskTypeDto = "TEXT" | "VISION" | "IMAGE";

export interface PromptDto {
  id: string;
  name: string;
  usedIn: string;
  status: string;
  taskType: PromptTaskTypeDto;
  tenantId: string | null;
  tenantName: string | null;
  labels: string[];
  createdAt: string;
  updatedAt: string;
  latestVersion: PromptVersionDto | null;
  /** Present when the list was filtered by `q`. */
  snippet?: string | null;
  defaultModel?: PromptServiceDefaultModelDto | null;
}

export interface PromptsListFilters {
  usedIn?: string;
  tenantId?: string;
  status?: string;
  taskType?: PromptTaskTypeDto;
  label?: string;
  q?: string;
}

// ─── API functions ───────────────────────────────────────────────────

export async function fetchPromptUsage(windowHours = 168): Promise<PromptUsageResponseDto> {
  const params = new URLSearchParams({ windowHours: String(windowHours) });
  const { data } = await authFetch<unknown>(`/prompts/usage?${params}`);
  return PromptUsageResponseSchema.parse(data);
}

export async function fetchPrompts(filters: PromptsListFilters = {}): Promise<PromptDto[]> {
  const params = new URLSearchParams();
  if (filters.usedIn) params.set("usedIn", filters.usedIn);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.status) params.set("status", filters.status);
  if (filters.taskType) params.set("taskType", filters.taskType);
  if (filters.label) params.set("label", filters.label);
  if (filters.q) params.set("q", filters.q);

  const qs = params.toString();
  const url = `/prompts${qs ? `?${qs}` : ""}`;
  const { data } = await authFetch<PromptDto[]>(url);
  return data;
}

export async function fetchPrompt(id: string): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`);
  return data;
}

export async function fetchPromptVersions(promptId: string): Promise<PromptVersionDto[]> {
  const { data } = await authFetch<PromptVersionDto[]>(`/prompts/${promptId}/versions`);
  return data;
}

export interface CreatePromptResult {
  prompt: PromptDto;
  globalFallbackCreated: boolean;
}

export async function createPrompt(body: {
  name: string;
  usedIn: string;
  tenantId?: string | null;
  content: string;
  model?: string | null;
  taskType?: PromptTaskTypeDto;
  labels?: string[];
  variables?: string[];
}): Promise<CreatePromptResult> {
  const res = await authFetch("/prompts", {
    method: "POST",
    body: JSON.stringify(body),
    rawResponse: true,
  });
  const json = (await res.json()) as { data: PromptDto; globalFallbackCreated?: boolean };
  return { prompt: json.data, globalFallbackCreated: json.globalFallbackCreated ?? false };
}

export async function updatePrompt(id: string, body: {
  name?: string;
  status?: string;
  taskType?: PromptTaskTypeDto;
  labels?: string[];
}): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return data;
}

/** Soft delete — the row is hidden from every list but stays recoverable. */
export async function deletePrompt(id: string): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`, {
    method: "DELETE",
  });
  return data;
}

export async function createPromptVersion(promptId: string, body: {
  content: string;
  model?: string | null;
  /** Sent with the version so taskType+model validate and persist atomically. */
  taskType?: PromptTaskTypeDto;
  variables?: string[];
  /** Version the editor loaded. The save is rejected when a newer write landed. */
  expectedVersionId?: string;
}): Promise<PromptVersionDto> {
  const { data } = await authFetch<PromptVersionDto>(`/prompts/${promptId}/versions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

export async function revertPromptVersion(promptId: string, versionId: string): Promise<PromptVersionDto> {
  const { data } = await authFetch<PromptVersionDto>(`/prompts/${promptId}/revert/${versionId}`, {
    method: "POST",
  });
  return data;
}

export async function fetchUsedInValues(): Promise<string[]> {
  const { data } = await authFetch<string[]>("/prompts/used-in-values");
  return data;
}

export async function fetchVariablesByUsedIn(usedIn: string): Promise<string[]> {
  const { data } = await authFetch<string[]>(`/prompts/variables/${encodeURIComponent(usedIn)}`);
  return data;
}

export async function fetchPromptLlmModels(): Promise<PromptLlmModelsResponseDto> {
  const { data } = await authFetch<PromptLlmModelsResponseDto>("/prompts/llm-models");
  return data;
}

export interface VariableUsageDto {
  id: string;
  name: string;
  usedIn: string;
  tenantId: string | null;
  tenantName: string | null;
}

export async function fetchPromptLabels(): Promise<string[]> {
  const { data } = await authFetch<string[]>("/prompts/labels");
  return data;
}

export async function fetchVariableUsages(name: string): Promise<VariableUsageDto[]> {
  const { data } = await authFetch<VariableUsageDto[]>(
    `/prompts/variables/${encodeURIComponent(name)}/usages`,
  );
  return data;
}

export interface PromptMatchDto {
  id: string;
  line: number;
  column: number;
  before: string;
  after: string;
  undefinedVariables?: string[];
}

export interface BulkReplacePromptDto {
  promptId: string;
  name: string;
  usedIn: string;
  tenantId: string | null;
  tenantName: string | null;
  isGlobal: boolean;
  versionId: string;
  matches: PromptMatchDto[];
  undefinedVariables?: string[];
  resultingContent: string;
}

export interface BulkReplaceRequest {
  find: string;
  replace: string;
  matchCase: boolean;
  wholeWord: boolean;
  regex: boolean;
  dryRun: boolean;
  selections: Array<{
    promptId: string;
    expectedVersionId?: string;
    matchIds?: string[];
  }>;
}

export async function previewBulkReplace(
  body: Omit<BulkReplaceRequest, "dryRun" | "selections"> & {
    selections?: BulkReplaceRequest["selections"];
  },
): Promise<BulkReplacePromptDto[]> {
  const { data } = await authFetch<{ prompts: BulkReplacePromptDto[] }>("/prompts/bulk-replace", {
    method: "POST",
    body: JSON.stringify({ ...body, dryRun: true, selections: body.selections ?? [] }),
  });
  return data.prompts;
}

export async function applyBulkReplace(
  body: Omit<BulkReplaceRequest, "dryRun">,
): Promise<{ versions: Array<{ promptId: string; versionId: string; version: number; created: boolean }> }> {
  const { data } = await authFetch<{
    versions: Array<{ promptId: string; versionId: string; version: number; created: boolean }>;
  }>("/prompts/bulk-replace", {
    method: "POST",
    body: JSON.stringify({ ...body, dryRun: false }),
  });
  return data;
}

export async function refreshPromptLlmModels(): Promise<PromptLlmModelsResponseDto> {
  const { data } = await authFetch<PromptLlmModelsResponseDto>("/prompts/llm-models/refresh", {
    method: "POST",
  });
  return data;
}
